import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useTranslation } from 'react-i18next';
import { ClientServicePricing } from '../../../data/models/client-service-pricing.model';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

const PREFIX = 'ServicePricingConflictDialog';

const classes = {
  content: `${PREFIX}-content`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    paddingTop: theme.spacing(2),
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface ServicePricingConflictDialogProps {
  open: boolean;
  conflicts: ClientServicePricing[];
  confirmActivate: () => void;
  cancelActivate: () => void;
}

export default function ServicePricingConflictDialog({
  open,
  conflicts,
  confirmActivate,
  cancelActivate,
}: ServicePricingConflictDialogProps) {
  const { t } = useTranslation('common');

  const handleCancel = () => {
    cancelActivate();
  };

  const handleActivate = () => {
    confirmActivate();
  };

  return (
    <RootDialog open={open} onClose={handleCancel}>
      <DialogTitle>{t('client.confirmActivate')}</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          {t('client.pricingConflict')}
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('client.name')}</TableCell>
                    <TableCell>{t('client.effectiveStart')}</TableCell>
                    <TableCell>{t('client.effectiveEnd')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conflicts.map((conflict) => (
                    <TableRow
                      key={conflict.clientServicePricingUUID!}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                      }}>
                      <TableCell component="th" scope="row">
                        {conflict.name}
                      </TableCell>
                      <TableCell>{conflict.effectiveStartDate}</TableCell>
                      <TableCell>{conflict.effectiveEndDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </LocalizationProvider>
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleActivate}>
          {t('client.activate')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
